// Copyright (c) 2023, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 Inc. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import * as React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import BackdropProgress from '../../BackdropProgress.js';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ConfirmationDialog from '../dialog/ConfirmationDialog';
import ClearIcon from "@material-ui/icons/Clear";
import CustomSnackbar from '../ui/Snackbars';
import Dialog from '@mui/material/Dialog';
import EditIcon from '@mui/icons-material/Edit';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import useHttp from '../../utils/http';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { adjustForTimezone, getCountryList, getDateForDB } from '../../utils/formatting';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { patchEmployeeTravelDocument, postTravelDocumentImage } from '../../utils/end-points.js';
import { FileUploader } from "react-drag-drop-files";
import { getTravelDocumentStatusColor, getTravelStatusColor } from '../../theme/status-colors.js';
import { IconButton } from "@material-ui/core";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { makeStyles } from '@mui/styles';
import { travelDocumentStatus, travelDocumentStatusArray, travelDocumentTypes, travelDocumentTypesArray, travelStatus, travelStatusArray } from '../../utils/const.js';
import { useState, useEffect } from 'react';
import "../../css/EditTravelDocumentDialog.scss"

const useStyles = makeStyles(() => ({
    paper: { minWidth: "700px" },
}));

function EditTravelDocumentDialog(props) {

    const classes = useStyles();
    const { open, onClose, document, image, refreshTravelDocumentList } = props
    const { handleRequest, handleFileRequest } = useHttp();
    const [documentType, setDocumentType] = useState(document.documentType)
    const [documentNumber, setDocumentNumber] = useState(document.documentNo)
    const [country, setCountry] = useState(null)
    const [issuedDate, setIssuedDate] = useState(adjustForTimezone(new Date(document.issuedDate)))
    const [expiryDate, setExpiryDate] = useState(document.expiryDate ? adjustForTimezone(new Date(document.expiryDate)) : null)
    const [status, setStatus] = useState(document.status)
    const [documentImage, setDocumentImage] = useState(image)
    const [isDocumentImageChanged, setDocumentImageChanged] = useState(null)
    const [isDocumentImageEditable, setDocumentImageEditable] = useState(false)
    const [isFormError, setFormError] = useState(false)
    const [isDateError, setDateError] = useState(false)
    const [isPageLoading, setPageLoading] = useState(false)
    const [snackbarData, setSnackbarData] = useState({ open: false, message: "", severity: "", onClose: null })
    const [confirmationDialogData, setConfirmationDialogData] = useState({ open: false, title: "", content: "", acceptLabel: "", declineLabel: "", acceptAlert: null, declineAlert: null })


    useEffect(() => {
        var selectedCountry = getCountryList().filter(country => country.countryCode === document.issuedCountry)
        if (selectedCountry.length > 0) {
            setCountry(selectedCountry[0])
        }
    }, [])

    const handleSubmit = () => {

        setDateError(false)
        setFormError(false)
        if (!country || (documentType !== travelDocumentTypes.OTHER_ID_DOCUMENT && !expiryDate) || !documentImage || !documentNumber || !documentType || !documentImage || !status) {
            setFormError(true)
            setSnackbarData({ message: "Please check the form again", open: true, severity: "error", onClose: onCloseSnackbar })
        }
        else {
            setFormError(false)
            if ((documentType !== travelDocumentTypes.OTHER_ID_DOCUMENT) && (issuedDate > expiryDate)) {
                setSnackbarData({ message: "Please check the dates again", open: true, severity: "error", onClose: onCloseSnackbar })
                setDateError(true)
                setFormError(true)
            }
            else {
                var contentMsg = "Please make sure the information are correct. Are you sure you want to edit this Document?"
                if (status === travelDocumentStatus.INACTIVE) {
                    contentMsg = "You are about to Inactivate this Travel Document. This can not be undone. Please make sure the information are correct. Are you sure you want to edit this Document?"
                }
                setConfirmationDialogData({
                    open: true,
                    title: "Edit Travel Document",
                    content: contentMsg,
                    acceptLabel: "Yes",
                    declineLabel: "No",
                    acceptAlert: saveDocument,
                    declineAlert: handleDeclineAlert
                })
            }
        }
    }

    const saveDocument = async () => {

        setConfirmationDialogData(data => ({ ...data, open: false }))
        setPageLoading(true)
        setDateError(false)

        const body = {
            email: document.email,
            id: document.id,
            documentType: documentType,
            documentNo: documentNumber,
            issuedCountry: country.countryCode,
            issuedDate: getDateForDB(issuedDate),
            expiryDate: getDateForDB(expiryDate),
            status: status
        }

        handleRequest(patchEmployeeTravelDocument, "PATCH", body, (response) => {
            if (response.id > 0) {
                if (isDocumentImageChanged) {
                    let fileExtension = documentImage.name.substring(documentImage.name.lastIndexOf('.') + 1, documentImage.name.length)
                    let fileName = country.countryCode + "." + fileExtension;

                    const reader = new FileReader()
                    reader.readAsDataURL(documentImage);
                    reader.onload = () => {
                        const encodedFile = btoa(reader.result)
                        const fileBody = {
                            encodedFile: encodedFile
                        }
                        handleRequest(postTravelDocumentImage.replace("$documentId", response.id).replace("$imageName", fileName), "POST", fileBody, (document) => {
                            if (document.id > 0) {
                                handleClose()
                                onClose()
                                refreshTravelDocumentList()
                            }
                            else {
                                setSnackbarData({ message: "Travel document image upload failed!", open: true, severity: "error", onClose: onCloseSnackbar })
                                setPageLoading(false)
                            }
                        }, (error) => {
                            console.error(error)
                            setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
                            setPageLoading(false)
                        })
                    };

                }
                else {
                    handleClose()
                    onClose()
                    refreshTravelDocumentList()
                    setPageLoading(false)
                }
            }
            else {
                setPageLoading(false)
                setSnackbarData({ message: "Travel document image upload failed!", open: true, severity: "error", onClose: onCloseSnackbar })
            }
        }, (error) => {
            setPageLoading(false)
            console.error(error)
            setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
            setPageLoading(false)
        })
    }


    const handleDocumentImage = async (file) => {
        setDocumentImage(file)
        setDocumentImageChanged(true)
    }

    const fileUploadSizeError = () => {
        console.error("Size cannot exceed 1MB")
        setSnackbarData({ message: "Size cannot exceed 1MB", open: true, severity: "error", onClose: onCloseSnackbar })
        setDocumentImage(null)
    }

    const handleClose = () => {
        onClose()
        setDocumentNumber(null)
        setCountry(null)
        setExpiryDate(null)
        setIssuedDate(null)
        setDocumentImage(null)
        setFormError(false)
        setDateError(false)
        onCloseSnackbar()
    }

    const handleDeclineAlert = () => {
        setConfirmationDialogData(data => ({ ...data, open: false }))
    }

    const onCloseSnackbar = () => {
        setSnackbarData({ ...snackbarData, open: false })
    }

    return (


        <Dialog onClose={() => handleClose()} open={open} classes={{ paper: classes.paper }} className="Edit-travel-document-dialog">

            <ConfirmationDialog confirmationDialogData={confirmationDialogData} />

            <CustomSnackbar snackbar={snackbarData} />

            <BackdropProgress open={isPageLoading} />

            <div className='Dialog-div'>

                <div className='Dialog-title'>Edit Travel Document</div>

                <div>
                    <div className='Fields-required'><span className='Required-span'>*</span> All fields are required</div>

                    <Grid container className='Grid-container'>
                        <Grid item md={4} className='Grid-item'><span className='Grid-item-label'>Email</span><span className='Grid-item-required'>*</span></Grid>
                        <Grid item md={8} className='Grid-item'>
                            <TextField disabled fullWidth size="small" label={document.email} />
                        </Grid>
                    </Grid>

                    <Grid container className='Grid-container'>
                        <Grid item md={4} className='Grid-item'><span className='Grid-item-label'>Document Type</span><span className='Grid-item-required'>*</span></Grid>
                        <Grid item md={8} className='Grid-item'>
                            <Autocomplete
                                value={document.documentType}
                                size="small"
                                noOptionsText='No options'
                                disablePortal
                                isOptionEqualToValue={(option, value) => option === value}
                                fullWidth
                                onChange={(event, value) => setDocumentType(value)}
                                options={travelDocumentTypesArray}
                                getOptionLabel={(option) => option}
                                renderOption={(props, option) => (
                                    <Box {...props}>{option}</Box>
                                )}
                                renderInput={(params) => <TextField  {...params} label="Document Type" error={isFormError && !documentType} />}
                            />
                        </Grid>
                    </Grid>

                    <Grid container className='Grid-container'>
                        <Grid item md={4} className='Grid-item'><span className='Grid-item-label'>Document Number</span><span className='Grid-item-required'>*</span></Grid>
                        <Grid item md={8} className='Grid-item'>
                            <TextField size="small" fullWidth label="Document Number" defaultValue={document.documentNo} onChange={(event) => setDocumentNumber(event.target.value)} error={isFormError && !documentNumber} />
                        </Grid>
                    </Grid>

                    <Grid container className='Grid-container'>
                        <Grid item md={4} className='Grid-item'><span className='Grid-item-label'>Issued Country</span><span className='Grid-item-required'>*</span></Grid>
                        <Grid item md={8} className='Grid-item'>
                            <Autocomplete
                                value={country}
                                size="small"
                                noOptionsText='No options'
                                disablePortal
                                isOptionEqualToValue={(option, value) => option.country === value.country}
                                fullWidth
                                onChange={(event, value) => setCountry(value)}
                                options={getCountryList()}
                                getOptionLabel={(option) => option.country}
                                renderOption={(props, option) => (
                                    <Box {...props}>{option.country}</Box>
                                )}
                                renderInput={(params) => <TextField  {...params} label="Issued Country" error={isFormError && !country} />}
                            />
                        </Grid>
                    </Grid>

                    <Grid container className='Grid-container'>
                        <Grid item md={4} className='Grid-item'><span className='Grid-item-label'>Issued Date</span><span className='Grid-item-required'>*</span></Grid>
                        <Grid item md={8} className='Grid-item'>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label="Issued Date"
                                    inputFormat='yyyy-MM-dd'
                                    mask='____-__-__'
                                    value={issuedDate}
                                    onChange={(value) => setIssuedDate(value)}
                                    InputProps={{
                                        endAdornment: (
                                            <IconButton onClick={() => setIssuedDate(null)} size="small">
                                                <ClearIcon />
                                            </IconButton>
                                        )
                                    }}
                                    InputAdornmentProps={{
                                        position: "start"
                                    }}
                                    renderInput={(params) => <TextField size="small" {...params} fullWidth error={(isFormError && !issuedDate) || isDateError} />}
                                />
                            </LocalizationProvider>
                        </Grid>
                    </Grid>

                    <Grid container className='Grid-container'>
                        <Grid item md={4} className='Grid-item'>
                            <span className='Grid-item-label'>Expiry Date</span>
                            {documentType !== travelDocumentTypes.OTHER_ID_DOCUMENT && <span className='Grid-item-required'>*</span>}
                        </Grid>
                        <Grid item md={8} className='Grid-item'>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label="Expiry Date"
                                    inputFormat='yyyy-MM-dd'
                                    mask='____-__-__'
                                    value={expiryDate}
                                    onChange={(value) => setExpiryDate(value)}
                                    InputProps={{
                                        endAdornment: (
                                            <IconButton onClick={() => setExpiryDate(null)} size="small">
                                                <ClearIcon />
                                            </IconButton>
                                        )
                                    }}
                                    InputAdornmentProps={{
                                        position: "start"
                                    }}
                                    renderInput={(params) => <TextField size="small" {...params} fullWidth error={(isFormError && documentType !== travelDocumentTypes.OTHER_ID_DOCUMENT && !expiryDate) || isDateError} />}
                                />
                            </LocalizationProvider>
                        </Grid>
                    </Grid>

                    <Grid container className='Grid-container'>
                        <Grid item md={4} className='Grid-item'><span className='Grid-item-label'>Status</span><span className='Grid-item-required'>*</span></Grid>
                        <Grid item md={8} className='Grid-item'>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select value={status} label="Status" onChange={(event) => setStatus(event.target.value)}>
                                    {travelDocumentStatusArray.length > 0 && travelDocumentStatusArray.filter(status => status !== travelDocumentStatus.EXPIRED).map(status => {
                                        return <MenuItem key={status} value={status}>
                                            <span className="Status-dot-dropdown" style={{ backgroundColor: (status == travelDocumentStatus.ACTIVE) ? getTravelDocumentStatusColor(true) : getTravelDocumentStatusColor(false) }}></span>{status}
                                        </MenuItem>
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Grid container className='Grid-container'>
                        <Grid item md={4} className='Grid-item'><span className='Grid-item-label'>Document Image</span><span className='Grid-item-required'>*</span></Grid>
                        <Grid item md={8} className='Grid-item'>
                            {(isDocumentImageEditable || !image) ? <div className={(isFormError && !documentImage) ? "File-upload-div-error" : "File-upload-div"}>
                                <FileUploader handleChange={file => (handleDocumentImage(file))} maxSize={1} onSizeError={() => fileUploadSizeError()}
                                    name="file" label="Browse or Drag and Drop Image. Please upload an image of the document details page (Max Size: 1Mb)." types={["JPG", "PNG", "JPEG"]} />
                            </div> : (
                                <div className='Grid-item'>
                                    <img id="image" src={image.src} alt={image.name} className='Document-image' />
                                    <Button size="small" variant="outlined" startIcon={<EditIcon />} color="primary" className='Edit-image-button' onClick={() => { setDocumentImageEditable(true) }} >Edit Image</Button>
                                </div>
                            )}

                        </Grid>
                    </Grid>

                    <div className='Button-grid'>
                        <Button variant="contained" color="secondary" className="Submit-button" onClick={() => handleSubmit()}>Submit</Button>
                        <Button variant="outlined" color="primary" className="Reset-button" onClick={() => handleClose()}>Cancel</Button>
                    </div>
                </div>
            </div>
        </Dialog >

    );
}

export default EditTravelDocumentDialog;
